import * as THREE from "three";
import Engine from "./Engine";
import Callout from "./Callout";
import SpriteBase from "./SpriteBase";
import AssetUtility from '../classes/AssetUtility';
import { RenderLayers } from '../globals';
import HighlightSphere from "../classes/HighlightSphere";
import StandbyOverlay from "../components/layout/StandbyOverlay";

let _geometry;
const tempPosition = new THREE.Vector3();
const tempRotation = new THREE.Quaternion();
const tempScale = new THREE.Vector3();
const identityQuat = new THREE.Quaternion();

class AudioSource extends SpriteBase
{
	static spriteScale = new THREE.Vector3(0.075, 0.075, 0.075);
	static audioSourceBank = [];
	static bufferMap = new Map();
	static clearData()
	{
		AudioSource.bufferMap.clear();
		AudioSource.audioSourceBank.length = 0;
	}

	static loadAudioClip(assetId)
	{
		return new Promise(function (resolve, reject)
		{
			if (!AudioSource.bufferMap.has(assetId))
			{
				StandbyOverlay.startStandbyMode("Loading Content");
				const assetUrl = AssetUtility.getRawAssetURL(assetId);
				new THREE.AudioLoader().load(
					assetUrl,
					function (buffer)
					{
						AudioSource.bufferMap.set(assetId, buffer);
						StandbyOverlay.stopStandbyMode();
						resolve(buffer);
					},
					function (xhr)
					{
						console.log("loading clip " + Math.round(xhr.loaded / xhr.total * 100.0) + "%");
					},
					function (error)
					{
						StandbyOverlay.stopStandbyMode();
						reject(new Error('Could not load Audio Clip ' + assetId + " " + error));
					}
				);
			}
			else
			{
				resolve(AudioSource.bufferMap.get(assetId));
			}
		});
	}

	constructor(audioListener)
	{
		super();

		if (_geometry === undefined)
		{
			_geometry = new THREE.BufferGeometry();

			const float32Array = new Float32Array([
				- 0.5, - 0.5, 0, 0, 0,
				0.5, - 0.5, 0, 1, 0,
				0.5, 0.5, 0, 1, 1,
				- 0.5, 0.5, 0, 0, 1
			]);

			const interleavedBuffer = new THREE.InterleavedBuffer(float32Array, 5);

			_geometry.setIndex([0, 1, 2, 0, 2, 3]);
			_geometry.setAttribute('position', new THREE.InterleavedBufferAttribute(interleavedBuffer, 3, 0, false));
			_geometry.setAttribute('uv', new THREE.InterleavedBufferAttribute(interleavedBuffer, 2, 3, false));

		}

		this.geometry = _geometry;
		this.material = AudioSource.spriteMaterial;
		this.center = new THREE.Vector2(0.5, 0.5);

		this.name = "Audio Source";
		this.userData.audio =
		{
			volume: 1.0,
			is3D: false,
			clip: "",
			range: 10,

		};

		if(audioListener && audioListener instanceof THREE.AudioListener)
		{
			this.listener = audioListener;
			this.audio = new THREE.Audio(this.listener);
			this.audio.setLoop(true);
			this.positionalAudio = new THREE.PositionalAudio(this.listener);
			this.positionalAudio.setDistanceModel('linear');
			this.positionalAudio.setLoop(true);
			this.activeAudioSource = this.audio;
		}

		const curve = new THREE.EllipseCurve(0, 0, 1, 1, 0, 2 * Math.PI);
		const points = curve.getPoints(32);
		const geometry = new THREE.BufferGeometry().setFromPoints(points);
		const material = new THREE.LineBasicMaterial({ color: 0x00ffff });

		this.helper = new THREE.Object3D();
		const circle1 = new THREE.Line(geometry, material);
		const circle2 = new THREE.Line(geometry.clone(), material);
		const circle3 = new THREE.Line(geometry.clone(), material);
		circle2.geometry.rotateX(THREE.MathUtils.degToRad(90));
		circle3.geometry.rotateY(THREE.MathUtils.degToRad(90));

		this.helper.visible = false;
		this.helper.add(circle1);
		this.helper.add(circle2);
		this.helper.add(circle3);
		this.add(this.helper);

		this.helper.traverse(function (child)
		{
			child.userData.hideInHierarchy = true;
			child.userData.ignoreExport = true;
		});

		this.traverse(function (child) { child.layers.set(RenderLayers.MainCameraOnly); });

		AudioSource.audioSourceBank.push(this);
	}

	clone()
	{
		return new this.constructor(this.listener).copy(this);
	}

	copy(source, recursive = true)
	{
		THREE.Object3D.prototype.copy.call(this, source, false);
		if (source.center !== undefined) this.center.copy(source.center);

		this.setAudioClip(source.userData.audio.clip);
		this.setAudioRange(source.userData.audio.range);

		if (recursive === true)
		{
			for (let i = 0; i < source.children.length; i++)
			{
				const child = source.children[i];
				if (!child.userData.hideInHierarchy || child instanceof Callout || child instanceof HighlightSphere)
				{
					this.add(child.clone());
				}
			}
		}

		return this;
	}

	playAudio()
	{
		if(this.activeAudioSource) this.activeAudioSource.play();
	}

	pauseAudio()
	{
		if(this.activeAudioSource) this.activeAudioSource.pause();
	}

	stopAudio()
	{
		if (this.activeAudioSource?.source) this.activeAudioSource.stop();
	}

	setAudioVolume(value)
	{
		this.userData.audio.volume = value;
		if(this.activeAudioSource) this.activeAudioSource.setVolume(this.userData.audio.volume);
	}

	getAudioVolume()
	{
		return this.userData.audio.volume;
	}

	setAudio3D(value)
	{
		if (this.userData.audio.is3D !== value)
		{
			const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
			this.helper.visible = value && isSelected;
			this.userData.audio.is3D = value;
			this.activeAudioSource = value ? this.positionalAudio : this.audio;
			this.setAudioVolume(this.userData.audio.volume);
			this.setAudioClip(this.userData.audio.clip);
		}
	}

	getAudio3D()
	{
		return this.userData.audio.is3D;
	}

	setAudioClip(value)
	{
		this.userData.audio.clip = value;
		if (value && this.activeAudioSource)
		{
			AudioSource.loadAudioClip(value).then((buffer) =>
			{
				if(buffer)
				{
					this.activeAudioSource.setBuffer(buffer);
					this.activeAudioSource.setVolume(this.userData.audio.volume);
					this.setAudioRange(this.userData.audio.range);
				}
			});
		}
	}

	getAudioClip()
	{
		return this.userData.audio.clip;
	}

	setAudioRange(value)
	{
		this.userData.audio.range = value;
		if(this.positionalAudio) this.positionalAudio.setMaxDistance(value);
	}

	getAudioRange()
	{
		return this.userData.audio.range;
	}

	onSelected()
	{
		this.helper.visible = this.userData.audio.is3D;
	}

	onDeselected()
	{
		this.helper.visible = false;
	}

	updateMatrixWorld(force)
	{
		if (this.matrixAutoUpdate) this.updateMatrix();

		if (this.matrixWorldNeedsUpdate || force)
		{

			if (this.parent === null)
			{

				this.matrixWorld.copy(this.matrix);

			}
			else
			{

				this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);

			}

			this.matrixWorldNeedsUpdate = false;

			force = true;

		}


		// update children
		const children = this.children;

		for (let i = 0, l = children.length; i < l; i++)
		{
			children[i].updateMatrixWorld(force);
		}

		//force scale to be 1 before rendering. we never want the sprite or the light to be scaled
		this.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
		this.matrixWorld.compose(tempPosition, tempRotation, AudioSource.spriteScale);

		if (this.userData.audio.is3D)
		{
			for (const child of this.helper.children)
			{
				child.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
				tempScale.set(this.userData.audio.range, this.userData.audio.range, this.userData.audio.range);
				child.matrixWorld.compose(tempPosition, identityQuat, tempScale);
			}
		}

	}
}

export default AudioSource;
